import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Typography,
} from "@mui/material";

const EVENT_TYPES = [
  { value: "interview", label: "Interview" },
  { value: "call", label: "Call" },
  { value: "meeting", label: "Meeting" },
  { value: "other", label: "Other" },
];

export default function EventScheduleModal({
  open,
  onClose,
  onSubmit,
  defaultAttendees = [],
  recruiterEmail = "",
  panelOptions = [],
  jobOrder,
}) {
  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setSummary("");
      setDescription("");
      setStart("");
      setEnd("");
      setAttendees(defaultAttendees.join(", "));
      setEventType("interview");
      setNotes("");
      setAttachment("");
      setError("");
    }
  }, [open, defaultAttendees]);
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  // Attendees as comma-separated string
  const [attendees, setAttendees] = useState(defaultAttendees.join(", "));
  const [eventType, setEventType] = useState("interview");
  const [notes, setNotes] = useState("");
  const [attachment, setAttachment] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    const attendeeList = attendees
      .split(",")
      .map((a) => a.trim())
      .filter((a) => a.length > 0);
    if (!summary || !start || !end || attendeeList.length === 0) {
      setError("Summary, start, end, and at least one attendee are required.");
      return;
    }
    // Validation: start < end
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      setError("Invalid date/time format.");
      return;
    }
    if (startDate >= endDate) {
      setError("Start time must be before end time.");
      return;
    }
    setError("");
    onSubmit({
      summary,
      description,
      start,
      end,
      attendees: attendeeList,
      event_type: eventType,
      notes,
      attachment,
      recruiter_email: recruiterEmail,
      job_order_id: jobOrder?.id,
    });
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schedule Event</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        <TextField
          label="Summary"
          fullWidth
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Start Time"
          type="datetime-local"
          fullWidth
          value={start}
          onChange={(e) => setStart(e.target.value)}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Time"
          type="datetime-local"
          fullWidth
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Event Type</InputLabel>
          <Select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            input={<OutlinedInput label="Event Type" />}
          >
            {EVENT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Attendees (comma-separated emails)"
          fullWidth
          value={attendees}
          onChange={(e) => setAttendees(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Enter one or more email addresses, separated by commas. All will receive calendar/email invites."
        />
        <TextField
          label="Notes"
          fullWidth
          multiline
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Attachment (URL)"
          fullWidth
          value={attachment}
          onChange={(e) => setAttachment(e.target.value)}
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
}
